import React, { useContext, useEffect, useState } from "react";
import moment from "moment-timezone";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IDashboardRefreshWidgetDataDTO from "../../../models/DTO/IDashboardRefreshWidgetDataDTO";
import DashboardWidgetTypeEnum from "../../../models/DashboardWidgetTypeEnum";
import IDashboardOnRefreshWidgetDataDTO from "../../../models/DTO/IDashboardOnRefreshWidgetDataDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import { Socket } from "socket.io-client";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import IDashboardItem from "../../../models/IDashboardItem";
import IDashboard from "../../../models/IDashboard";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import IDashboardGetWidgetDataDTO from "../../../models/DTO/IDashboardGetWidgetDataDTO";
import IDashboardOnGetWidgetDataDTO from "../../../models/DTO/IDashboardOnGetWidgetDataDTO";
import styled from "styled-components";
import { OwIconButton } from "../../shared/OwIconButton/OwIconButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IOrder from "../../../models/IOrder";
import { DueDateCalendarDay } from "./DueDateCalendarDay/DueDateCalendarDay";

type Props = {
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    inEditor: boolean;
    widget?: IDashboardItem;
    removeWidget?: () => void;
    updateWidgetFn?: (widgetData: any, widgetUuid: string) => void;
    dashboard?: IDashboard;
};

export const DueDateCalendarWidget = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const getWidgetData = () => {
        if (props.widget && props.dashboard && !isLoading) {
            const payload: IDashboardGetWidgetDataDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                widgetType: DashboardWidgetTypeEnum.DUE_DATE_CALENDAR,
                widgetUuid: props.widget?.uuid,
                widgetData: {
                    startDate: startDate.format("YYYY-MM-DD"),
                    endDate: endDate.format("YYYY-MM-DD"),
                },
            };

            props.socket.emit(EndpointsEnum.DashboardGetWidgetData, payload);
        }

        setIsLoading(true);
    };

    const onGetWidgetData = (data: IDashboardOnGetWidgetDataDTO) => {
        if (data.widgetUuid === props.widget?.uuid) {
            setIsLoading(false);
            setOrders(data.widgetData);
        }
        // const notification: INotification = {
        //     title: "sales_summary_queued_for_update",
        //     message: "please_allow_5_to_8_minutes_for_the_data_to_update",
        //     level: NotificationLevelEnum.success,
        //     position: NotificationPositionEnum.bc,
        // };
        //
        // props.notifyFn(null, notification);
    };

    const calculateDaysArray = () => {
        const days = endDate.diff(startDate, "days");
        const _daysArray: moment.Moment[] = [];

        for (let i = 0; i <= days; i++) {
            _daysArray.push(moment(startDate).add(i, "days"));
        }

        return _daysArray;
    };

    const [startDate, setStartDate] = useState<moment.Moment>(moment());
    const [endDate, setEndDate] = useState<moment.Moment>(moment().add(13, "days"));
    const [daysArray, setDaysArray] = useState<moment.Moment[]>(calculateDaysArray());
    const [isLoading, setIsLoading] = useState(false);
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [isTodayStartDate, setIsTodayStartDate] = useState(true);

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.DashboardOnGetWidgetData, fn: onGetWidgetData }];

    useEffect(() => {
        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        getWidgetData();

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);
    const prevTwoWeeks = () => {
        setIsLoading(false);
        setStartDate(startDate.subtract(14, "days"));
        setEndDate(endDate.subtract(14, "days"));
        const _daysArray = calculateDaysArray();
        setDaysArray(_daysArray);
        if (startDate.diff(moment(), "days") === 0) {
            setIsTodayStartDate(true);
        }
        getWidgetData();
    };

    const nextTwoWeeks = () => {
        setIsLoading(false);
        setStartDate(startDate.add(14, "days"));
        setEndDate(endDate.add(14, "days"));
        const _daysArray = calculateDaysArray();
        setDaysArray(_daysArray);
        setIsTodayStartDate(false);
        getWidgetData();
    };

    return (
        <WidgetContainer className={"DueDateCalendarWidget widget h-100 d-flex flex-column justify-content-start"}>
            {props.inEditor ? (
                <div>
                    <div className={"w-100"}>
                        <div>
                            <OwIconButton
                                iconClass={"fas fa-trash-alt"}
                                iconColorClass={"text-danger"}
                                fn={() => {
                                    props.removeWidget!();
                                }}
                            />
                        </div>
                        <div>
                            <OwLabel title={"due_date_calendar"} />
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {isLoading ? (
                        <LoadingDiv>
                            <OwLabel title={"loading"} leftIconClass={"fad fa-spinner fa-spin"} />
                        </LoadingDiv>
                    ) : (
                        <div className={"w-100"}>
                            <HeaderDiv>
                                <OwLabel title={"due_date_calendar"} isBold={true} />
                                <div className={"d-flex"}>
                                    {isTodayStartDate ? null : (
                                        <div className={"text-small me-4 pointer"} onClick={prevTwoWeeks}>
                                            <i className={"fas fa-arrow-left"} />{" "}
                                            <OwLabel title={"prev_two_weeks"} isBold={true} className={"pointer"} />
                                        </div>
                                    )}
                                    <div className={"text-small pointer"} onClick={nextTwoWeeks}>
                                        <OwLabel title={"next_two_weeks"} isBold={true} className={"pointer"} />{" "}
                                        <i className={"fas fa-arrow-right"} />
                                    </div>
                                </div>
                            </HeaderDiv>
                            <DueDateCalendarDayContainer>
                                {daysArray.map((day: moment.Moment, index) => {
                                    const ordersForDay = orders.filter((order: IOrder) => {
                                        return moment(order.due_date).isSame(day, "day");
                                    });

                                    return (
                                        <DueDateCalendarDay key={day.format("YYYY-MM-DD")} orders={ordersForDay} day={day} index={index} />
                                    );
                                })}
                            </DueDateCalendarDayContainer>
                        </div>
                    )}
                </>
            )}
        </WidgetContainer>
    );
};

const WidgetContainer = styled.div`
    padding: 0.5rem;
    justify-content: start !important;
    align-items: start !important;
    height: 100%;

    input {
        width: 100%;
    }

    .position-relative {
        width: 100%;
    }
`;

const HeaderDiv = styled.div`
    height: 30px;
    display: flex;
    justify-content: space-between;
`;

const LoadingDiv = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.5);
`;

const DueDateCalendarDayContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
`;
